import { useEffect, useState } from "react";
import { capitalize, integerName } from "../../../app/utils";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import SimplePagination from "../../../components/SimplePagination";
import { CreatePeerReviewGroupButton } from "../CreatePeerReviewGroup";
import { RandomizePeerReviewGroupsButton } from "../RandomizePeerReviewGroups";
import { ReviewGroupWriterModel } from "../peerReviewGroupsApiSlice";
import { DefaultGroupsButton } from "./UseDefaultGroupsButton";
import CoursePromptGroupsTable from "./CoursePromptGroupsTable";
import {
	CoursePromptsGroupInfo,
	CoursePromptReviewGroupsViewModel,
	CoursePromptReviewGroup,
} from "./coursePromptReviewGroupsApiSlice";

interface CoursePromptReviewGroupsProps {
	groupsData: CoursePromptReviewGroupsViewModel;
	updateGroups: (groups: CoursePromptsGroupInfo) => void;
}

export default function CoursePromptReviewGroups(props: CoursePromptReviewGroupsProps) {
	const [groups, setGroups] = useState<CoursePromptReviewGroup[]>(
		props.groupsData.coursePromptGroups.groupsPage
	);
	const [availablePool, setAvailablePool] = useState<ReviewGroupWriterModel[]>(
		props.groupsData.coursePromptGroups.availableWriterPool
	);
	const [poolVersion, setPoolVersion] = useState(0);
	const [canCreateGroup, setCanCreateGroup] = useState(false);

	const pageSize = 5;
	const [page, setPage] = useState<number>(0);

	const getPagedGroups = (items: CoursePromptReviewGroup[]): CoursePromptReviewGroup[] => {
		const start = page * pageSize;
		return items.slice(start, start + pageSize);
	};

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	const statusMessage = () => {
		if (!props.groupsData) return "";
		const reviewGroups = groups?.length ?? 0;
		const pool = availablePool?.length ?? 0;
		if (reviewGroups == 0 && pool == 0) return "No students to assign.";
		if (pool == 0) return "All students are assigned to prompt peer review groups.";
		if (reviewGroups == 0 && pool == 1)
			return "At least two students are needed for peer reviews.";
		if (reviewGroups == 0) return "No prompt peer reviews groups have been created.";
		if (pool == 1) return "One student has not been assigned to a prompt peer review group.";
		return `${capitalize(integerName(pool))} students have not been assigned to a prompt peer review group.`;
	};

	function handleUseDefault() {
		setGroups(props.groupsData.defaultGroups.groupsPage);
		setAvailablePool(props.groupsData.defaultGroups.availableWriterPool);
		setPage(0);
	}

	function handleAddGroup(newGroup: CoursePromptReviewGroup) {
		// Add new group to the list
		setGroups((prevGroups) => [...prevGroups, newGroup]);

		// Remove selected students from available pool
		setAvailablePool((prevPool) =>
			prevPool.filter((writer) => !newGroup.studentIds.includes(writer.id))
		);
	}

	function handleRemoveGroup(uniqueId: string) {
		// Remove group from the list
		setGroups((prevGroups) => prevGroups.filter((g) => g.uniqueId !== uniqueId));

		// Add writers from removed group into the available pool
		const groupToRemove = groups.find((g) => g.uniqueId === uniqueId);
		const writersToAdd = props.groupsData.allWriters.filter((w) =>
			groupToRemove?.studentIds.includes(w.id)
		);
		setAvailablePool((prevPool) =>
			[...prevPool, ...writersToAdd].sort((a, b) => a.fullName.localeCompare(b.fullName))
		);
	}

	function handleEditGroup(updatedGroup: CoursePromptReviewGroup) {
		// Update the group in the list
		setGroups((prevGroups) =>
			prevGroups.map((group) =>
				group.uniqueId === updatedGroup.uniqueId ? updatedGroup : group
			)
		);

		// Find the old group from current state
		const oldGroup = groups.find((g) => g.uniqueId === updatedGroup.uniqueId);

		// Update available pool: add back students from old group, remove students from new group
		if (oldGroup) {
			// Students that were in the group, but are not anymore
			const studentsToAddBack = oldGroup.studentIds.filter(
				(id) => !updatedGroup.studentIds.includes(id)
			);

			// Students that are in the group now, but were not originally should be removed from pool
			const studentsToRemove = updatedGroup.studentIds.filter(
				(id) => !oldGroup.studentIds.includes(id)
			);

			setAvailablePool((prevPool) => {
				// Add back students that were removed from the group
				const writersToAdd = props.groupsData.allWriters.filter((w) =>
					studentsToAddBack.includes(w.id)
				);
				const poolWithAddedBack = [...prevPool, ...writersToAdd];

				// Remove students that were newly added to the group
				return poolWithAddedBack
					.filter((writer) => !studentsToRemove.includes(writer.id))
					.sort((a, b) => a.fullName.localeCompare(b.fullName));
			});
		}
	}

	function handleRandomize(randomGroups: CoursePromptReviewGroup[]) {
		setGroups(randomGroups);
		setAvailablePool([]);
		setPage(0);
	}

	useEffect(() => {
		setCanCreateGroup(availablePool.length > 1);
		setPoolVersion((prev) => prev + 1);

		props.updateGroups({ groupsPage: groups, availableWriterPool: availablePool });
	}, [availablePool, groups]);

	return (
		<div>
			{props.groupsData.canChangeGroups ? (
				<div className="flex justify-end">
					<CreatePeerReviewGroupButton
						makingCoursePromptGroup={true}
						groupNames={groups.map((g) => g.name)}
						availablePool={availablePool}
						disabled={!canCreateGroup}
						onCreateCoursePromptGroup={handleAddGroup}
					/>
					<DefaultGroupsButton
						onClick={handleUseDefault}
						disabled={props.groupsData.defaultGroups.groupsPage.length == 0}
					/>
					<RandomizePeerReviewGroupsButton
						hasGroups={groups.length > 0}
						makingCoursePromptGroups={true}
						onRandomizeCoursePromptGroups={handleRandomize}
						disabled={!props.groupsData.canRandomizeGroups}
					/>
				</div>
			) : (
				<AlertMessage
					className="text-sm"
					type={AlertLevel.Warning}
					message={
						"Students have started requesting reviews for this prompt, so the groups can no longer be changed."
					}
					showIcon={true}
				/>
			)}
			<CoursePromptGroupsTable
				groups={getPagedGroups(groups)}
				allWriters={props.groupsData.allWriters}
				availablePool={availablePool}
				poolVersion={poolVersion}
				canChangeGroups={props.groupsData.canChangeGroups}
				onEditCoursePromptGroup={handleEditGroup}
				removeCoursePromptGroup={handleRemoveGroup}
			/>
			<p className="text-right">{statusMessage()}</p>
			<SimplePagination
				page={page}
				pageSize={pageSize}
				itemCount={groups.length}
				onChange={(page: number) => handlePageChange(page)}
				className={"border-none pt-2"}
			/>
		</div>
	);
}
